import { RootState } from "integrations/redux"
import { Map } from "react-map-gl"
import { ComponentProps, useMemo } from "react"
import { createSelector } from "@reduxjs/toolkit"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import { Icon } from "@nova/protobuf"
import { AnyAction } from "@nova/constants"
import { MapStyleNames } from "./module"
import { getUnitAdjustedStyleJSON, mapThemes } from "../map-themes/mapThemes"
import { selectPictureInPictureId } from "plugins/shared/playback/state/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"

const root = (state: RootState) => state.map.control

export const selectActivatedFlightId = (state: RootState): string | undefined =>
    root(state).entities.flightId

export const selectActivatedFireId = (state: RootState): string | undefined =>
    root(state).entities.fireId

export const selectActivatedFire = createSelector(
    [selectActivatedFireId, selectAllFires],
    (id, fires) => {
        return id ? fires[id] : undefined
    }
)

export const selectSelectedMapIcon = (state: RootState): Icon =>
    root(state).entities.selectedMapIcon!

export const selectClipboardMapIcon = (state: RootState) =>
    root(state).entities.clipboardMapIcon as AnyAction

const IdAndSourceMappings = {
    mapLabels: "labels",
    buildings: "buildings",
    contours: "contours",
    shading: "hillshade",
}
type IdSourceMappable = keyof typeof IdAndSourceMappings

export const selectMapFeatures = (state: RootState) => root(state).mapFeatures
export const selectWeatherEnabled = (state: RootState) => root(state).weather.enabled
export const selectWeatherTimeline = (state: RootState) => root(state).weather.timeline
export const selectWeatherPanel = (state: RootState) => root(state).weather.panel
export const selectWeatherPreferences = (state: RootState) => root(state).weather.preferences

const LIGHT_MAP_OPACITY_FACTOR = 0.9
export function useWeatherHeatmapOpacity() {
    const panel = useAppSelector(selectWeatherPanel)
    const background = useLocalStorageValue("mapBackground")
    const dimForLightMap =
        (panel.mode === "wind" || panel.mode === "precipitation") &&
        background !== MapStyleNames.Satellite
    return dimForLightMap ? panel.opacity * LIGHT_MAP_OPACITY_FACTOR : panel.opacity
}
export const selectMapOverlayStatus = (state: RootState) => root(state).overlayStatus
export const selectMapOverlaySceneTimes = (state: RootState) => root(state).overlaySceneTimes

function computeMapStylesJSON(
    features: ReturnType<typeof selectMapFeatures>,
    background: MapStyleNames
) {
    const mapTheme = mapThemes[background]
    return getUnitAdjustedStyleJSON({
        ...mapTheme,
        layers: mapTheme.layers.filter((layer) => {
            const id = layer.id
            const source = "source" in layer ? layer.source : undefined
            const matchedFeatures = Object.keys(features).filter(
                (feature) =>
                    id.includes(IdAndSourceMappings[feature as IdSourceMappable]) ||
                    source?.includes(IdAndSourceMappings[feature as IdSourceMappable])
            )
            if (!matchedFeatures.length) {
                return true
            }
            return features[matchedFeatures[0] as keyof typeof features]
        }),
    })
}
export function useMapStylesJSON() {
    const features = useAppSelector(selectMapFeatures)
    const background = useLocalStorageValue("mapBackground")
    return useMemo(() => computeMapStylesJSON(features, background), [features, background])
}
export function useOverviewMapStylesJSON() {
    const features = useAppSelector(selectMapFeatures)
    const background = useLocalStorageValue("overviewMapBackground")
    return useMemo(() => computeMapStylesJSON(features, background), [features, background])
}

export const selectControllerOverrides = (state: RootState): ComponentProps<typeof Map> =>
    root(state).controllerOverrides

export const selectIsPictureInPicture = createSelector(
    [root, selectPictureInPictureId],
    (s, videoPipId) => s.isPictureInPicture || !!videoPipId
)

/** @deprecated */
export const selectLayerDropdownOpen = (state: RootState): boolean => root(state).layerDropdownOpen
